import React, { FC, useEffect, useMemo, useState } from 'react';

import { RootStoreType } from 'src/types/store';
import { useDispatch, useSelector } from 'react-redux';
import translate from '@translate';
import { setLocationParams } from 'src/resources/location/reducer';
import TableSimple from 'src/components/TableSimple';
import { allColumns } from './constants';
import { CustomRenderType, TableRowType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import { getUsersListFetch, activateUserFetch, deactivateUserFetch } from 'src/resources/usersList/reducer';
import UserAvatar from 'src/components/UserAvatar/ïndex';
import { UserDetailIcon, PermissionsIcon, BellIcon } from 'src/components/SkladonIcon/SmallIcons';
import SimpleSwitch from 'src/components/SimpleSwitch';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import { langCodes } from 'src/resources/constantsCodes/constants';
import { ProviderType } from 'src/types/provider';

export interface UsersListContainerPropsType {}

const ESHOPS_TEXT_MAX_LENGTH = 35;

const getProviderDisplayName = (provider: ProviderType): string => provider.COMPANY_NAME || provider.ESHOP_KEY || '';

const getProvidersText = (providers: ProviderType[] | undefined): string =>
    providers?.map(getProviderDisplayName).filter(Boolean).join(', ') || '';

const truncateText = (text: string, maxLength: number): string =>
    text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

const UsersListContainer: FC<UsersListContainerPropsType> = () => {
    const dispatch = useDispatch();

    const loading = useSelector((state: RootStoreType) => state.usersList.loading);
    const usersData = useSelector((state: RootStoreType) => state.usersList.data);
    const activeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.activeCodes);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const loggedSkladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);

    const [usersList, setUsersList] = useState<TableRowType[]>([]);
    const [roleCodes, setRoleCodes] = useState<ConstantsCodeType[]>([]);
    const [eshopCodes, setEshopCodes] = useState<ConstantsCodeType[]>([]);

    const permissionCreate = useCheckPermission('USER.CREATE');
    const permissionDeatil = useCheckPermission('USER.DETAIL');
    const permissionUpdate = useCheckPermission('USER.UPDATE');
    const disableUserPermissionsDetail = !useCheckPermission(['USER.PERMISSION.LIST', 'ROLE.LIST']);
    const disableUserNotificationsDetail = !useCheckPermission(['USER.NOTIFICATION.LIST', 'NOTIFICATION.LIST']);

    const tableColumns = useMemo(
        () => (loggedSkladonUser ? allColumns : allColumns.filter((column) => column.key !== 'SKLADON_USER')),
        [loggedSkladonUser]
    );

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('users.title') }));
    }, [dispatch]);

    useEffect(() => {
        if (!usersData) {
            dispatch(getUsersListFetch());
        } else {
            const rolesObj: Record<string, boolean> = {};
            const eshopsObj: Record<string, boolean> = {};

            setUsersList(
                usersData.map((user) => {
                    const providersText = getProvidersText(user.providers);
                    providersText.split(', ').forEach((eshop) => {
                        if (eshop) {
                            eshopsObj[eshop] = true;
                        }
                    });

                    return {
                        ...user,
                        rolesText: user.roles
                            ? user.roles.map((role) => {
                                  rolesObj[role.ROLE_NAME] = true;
                                  return role.ROLE_NAME;
                              })
                            : '',
                        USERS_PROVIDERS: providersText,
                        USERS_PROVIDERS_FULL: providersText,
                    };
                })
            );
            setRoleCodes(Object.keys(rolesObj).map((key) => ({ value: key, label: key })));
            setEshopCodes(Object.keys(eshopsObj).map((key) => ({ value: key, label: key })));
        }
    }, [usersData, dispatch]);

    const reloadList = () => {
        dispatch(getUsersListFetch());
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'photo':
                return <UserAvatar user={row} />;
            case 'active':
                return (
                    <SimpleSwitch
                        value={!!row.active}
                        onChange={(value) => {
                            if (value) {
                                dispatch(activateUserFetch(row.USER_ID));
                            } else {
                                dispatch(deactivateUserFetch(row.USER_ID));
                            }
                        }}
                        disabled={!permissionUpdate}
                    />
                );
            case 'SKLADON_USER': {
                const label = boolNumberCodes.find((item) => item.value === row.SKLADON_USER)?.label;
                return label ? translate(label) : '';
            }
            case 'USERS_PROVIDERS': {
                const fullText = String(row.USERS_PROVIDERS_FULL || row.USERS_PROVIDERS || '');
                const displayText = truncateText(fullText, ESHOPS_TEXT_MAX_LENGTH);

                if (!fullText || fullText === displayText) {
                    return displayText;
                }

                return <span>{displayText}</span>;
            }
            default:
                return undefined;
        }
    };

    return (
        <>
            <TopBlock reloadList={reloadList} permissionCreate={permissionCreate} />
            <TableSimple
                rowIdKey="USER_ID"
                rows={usersList}
                loading={loading}
                columsLabelPrefix="users.table"
                allColumns={tableColumns}
                initSort={{ column: 'USER_ID', direction: 'DESC' }}
                rowActions={[
                    {
                        icon: <UserDetailIcon />,
                        label: translate('users.action.userDetail'),
                        getLink: (row) => `/users/${row.USER_ID}/`,
                        hiddenFce: () => !permissionDeatil,
                    },
                    {
                        icon: <PermissionsIcon />,
                        label: translate('users.action.permissionsDetail'),
                        getLink: (row) => `/users/permissions/${row.USER_ID}/`,
                        hiddenFce: () => disableUserPermissionsDetail,
                    },
                    {
                        icon: <BellIcon />,
                        label: translate('users.action.notificationsDetail'),
                        getLink: (row) => `/users/notifications/${row.USER_ID}/`,
                        hiddenFce: () => disableUserNotificationsDetail,
                    },
                ]}
                selectOptions={{
                    activeCodes,
                    roleCodes,
                    boolNumberCodes,
                    eshopCodes,
                    langCodes,
                }}
                customRender={customRender}
                fullHeight
                optionsSessionName="usersTable"
                userSettingsName="showUsersTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
            />
        </>
    );
};

export default UsersListContainer;
